import Box from '@/common/components/Box';
import Button from '@/common/components/Button';
import Checkbox from '@/common/components/Checkbox';
import GoBackButton from '@/common/components/GoBackButton';
import TextArea from '@/common/components/TextArea';
import TextField from '@/common/components/TextField';
import { DateInput } from '@/common/components/DateInput/DateInput';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import { FileUploader } from '@/common/components/FileUploader';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import { ErrorMessage } from '@/common/components/ErrorMessage';
import { FormTransformers } from '@/common/utils/formTransformers';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ChevronsRight, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTmaisBanner } from '@/features/tmaisBanners/hooks/useTmaisBanner';
import { useUpdateTmaisBanner } from '@/features/tmaisBanners/hooks/useUpdateTmaisBanner';
import { updateTmaisBannerSchema, UpdateTmaisBannerValidationSchema } from '@/features/tmaisBanners/schemas/update.schema';
import { bannerFileConstraints } from '@/features/tmaisBanners/schemas/create.schema';
import { useQueryClient } from '@tanstack/react-query';
import { extractDateTime } from '@/common/utils/dateUtils';
import { useGoBack } from '@/common/hooks/useGoBack';

const TmaisBannerUpdate = () => {
  const { id } = useParams();
  const goBack = useGoBack();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    data: banner,
    dataUpdatedAt: bannerUpdatedAt,
    isPending: isFetching,
    error = {} as any,
    refetch
  } = useTmaisBanner({ id });

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    reset,
    setError: setFormError,
    formState: { errors },
  } = useForm<UpdateTmaisBannerValidationSchema>({
    resolver: zodResolver(updateTmaisBannerSchema),
    defaultValues: { add_expiration: false },
  });

  const { mutate: updateBanner, isPending: isUpdating } = useUpdateTmaisBanner({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tmaisBanners"] });
      queryClient.invalidateQueries({ queryKey: ["tmaisBanner", id] });
      goBack({ fallback: '/tmais-banners' });
    },
    onError: FormTransformers.errorsTransformer<UpdateTmaisBannerValidationSchema>({
      setError: (field, error) => setFormError(field, error),
      setMessages,
    }),
  });

  const watchAddExpiration = watch('add_expiration');

  useEffect(() => {
    if (banner) {
      const hasExpiration = !!banner.expires_at;

      reset({
        title: banner.title || '',
        description: banner.description || '',
        mobile_redirect_to: banner.mobile_redirect_to || '',
        active_at_date: extractDateTime(banner.active_at, 'date'),
        active_at_time: extractDateTime(banner.active_at, 'time'),
        add_expiration: hasExpiration,
        expires_at_date: hasExpiration ? extractDateTime(banner.expires_at, 'date') : undefined,
        expires_at_time: hasExpiration ? extractDateTime(banner.expires_at, 'time') : undefined,
      });
    }
  }, [bannerUpdatedAt, reset]);

  useEffect(() => {
    if (!watchAddExpiration) {
      setValue('expires_at_date', undefined);
      setValue('expires_at_time', undefined);
      clearErrors(['expires_at_date', 'expires_at_time']);
    }
  }, [watchAddExpiration, setValue, clearErrors]);

  const onSubmit: SubmitHandler<UpdateTmaisBannerValidationSchema> = (values) => {
    if (!id) return;
    updateBanner({ id, form: values });
  };

  const isWorking = isFetching || isUpdating;

  return (
    <ErrorMessageWrapper statusCode={error?.status} refetchFunction={refetch}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback={`/tmais-banners`} />
            <p className="text-white-400 font-normal">Banner</p>
            <ChevronsRight className="text-white-400" />
            <h2>Editar banner</h2>
          </div>

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline-error"
              disabled={isWorking}
              onClick={() => goBack({ fallback: '/tmais-banners' })}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              isLoading={isUpdating}
              disabled={isWorking}
            >
              Salvar alterações
            </Button>
          </div>
        </div>

        <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
          <p className="text-xl font-bold">Detalhes do Banner</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <DateInput
              fieldName="active_at_date"
              control={control as any}
              label="Data de envio"
              markAsRequired
              errors={errors}
            />

            <TextField
              label="Hora para envio"
              type="time"
              markAsRequired
              errorMessage={errors.active_at_time?.message}
              disabled={isWorking}
              LeftIcon={<Clock className="w-5 h-5 text-white-400" />}
              {...register('active_at_time')}
            />
          </div>

          <TextField
            label="Redirecionar clique para"
            markAsRequired
            errorMessage={errors.mobile_redirect_to?.message}
            disabled={isWorking}
            {...register('mobile_redirect_to')}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch mb-2">
            <Controller
              control={control}
              name="file"
              render={({ field }) => {
                const currentFileUrl = (banner as any)?.file?.url;
                const newFilePreview = field.value instanceof File ? URL.createObjectURL(field.value) : null;
                const displayUrl = newFilePreview || (!field.value && currentFileUrl ? currentFileUrl : null);

                return (
                  <div className="flex flex-col w-full h-full min-h-[220px]">
                    {displayUrl && (
                      <div className="relative mb-2 w-full h-36 bg-neutral-op-8 rounded-md overflow-hidden shrink-0">
                        <img src={displayUrl} alt="Preview do banner" className="absolute inset-0 w-full h-full object-cover opacity-60 hover:opacity-100 transition-opacity" />
                      </div>
                    )}

                    <div className="flex-1 w-full h-full bg-neutral-op-8 border border-transparent rounded-md transition-colors
                      [&>div]:w-full [&>div]:h-full [&>div]:border-none [&>div]:!bg-transparent [&>div]:m-0 [&>div]:p-0
                      [&_label]:w-full [&_label]:h-full [&_label]:flex [&_label]:flex-col [&_label]:justify-center [&_label]:items-center [&_label]:border-none [&_label]:!bg-transparent [&_label]:m-0
                    ">
                      <FileUploader
                        title={field.value?.name ? `Novo arquivo: ${field.value.name}` : "Alterar imagem (Opcional)"}
                        accept=".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif"
                        handleChange={(file) => {
                          field.onChange(file);
                          clearErrors('file');
                        }}
                        fileName={field.value?.name}
                        maxFileSizeInMb={bannerFileConstraints.maxSizeInMb}
                        disabled={isWorking}
                        setError={(message) => {
                          if (message) setFormError('file', { message });
                          else clearErrors('file');
                        }}
                      />
                    </div>
                    {errors.file?.message && (
                      <div className="mt-2">
                        <ErrorMessage name="file" message={errors.file?.message} />
                      </div>
                    )}
                  </div>
                );
              }}
            />

            <div className="flex flex-col gap-4 h-full">
              <TextField
                label="Nome do banner"
                markAsRequired
                errorMessage={errors.title?.message}
                disabled={isWorking}
                {...register('title')}
              />

              <TextArea
                label="Descrição interna"
                markAsRequired
                errorMessage={errors.description?.message}
                disabled={isWorking}
                className="flex-1 h-full resize-none"
                rows={5}
                {...register('description')}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 mt-2">
            <Checkbox
              label="Adicionar data de expiração"
              className="text-white-800"
              disabled={isWorking}
              {...register('add_expiration')}
            />

            {watchAddExpiration && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DateInput
                  fieldName="expires_at_date"
                  control={control as any}
                  label="Data de expiração"
                  markAsRequired
                  errors={errors}
                />
                <TextField
                  label="Hora para expiração"
                  type="time"
                  markAsRequired
                  errorMessage={errors.expires_at_time?.message}
                  disabled={isWorking}
                  LeftIcon={<Clock className="w-5 h-5 text-white-400" />}
                  {...register('expires_at_time')}
                />
              </div>
            )}
          </div>
        </Box>

        {messages && <ModalMessageSimple messages={messages as string[]} setMessages={setMessages as any} isError />}
      </form>
    </ErrorMessageWrapper>
  );
};

export default TmaisBannerUpdate;
